import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import {
  Dialog,
  Box,
  Typography,
  Stack,
  Button,
  CircularProgress,
} from '@mui/material'
import * as Icons from '@mui/icons-material'
import { TpToggleButtonGroup } from '~/components/tp-toggle-button-group'
import { TpToggleButton } from '~/components/tp-toggle-button'
import { CropTimeline } from '~/crop/crop-schedule/crop-timeline/crop-timeline'
import { useExportPdf } from './use-export-pdf'

// Width of the hidden export render container — matches landscape letter at 96 DPI minus margins
const EXPORT_WIDTH_PX = 1050

const DEFAULT_SETTINGS = {
  groupStrategy: 'none',
  sortStrategy: 'alphabetical',
  viewStrategy: 'phases',
  showOrgTasks: true,
  showSuccessions: false,
}

export const ExportTimelineModal = ({ open, onClose, farmName, farmLat, farmLng }) => {
  const exportRef = useRef(null)
  const [settings, setSettings] = useState(DEFAULT_SETTINGS)
  const [ready, setReady] = useState(false)

  // Give the hidden timeline time to render before allowing export
  useEffect(() => {
    if (!open) {
      setReady(false)
      return
    }
    const timer = setTimeout(() => setReady(true), 1800)
    return () => clearTimeout(timer)
  }, [open])

  const { exportPdf, isExporting } = useExportPdf({ exportRef, farmName, farmLat, farmLng })

  const handleExport = async () => {
    await exportPdf()
    onClose()
  }

  const set = (key) => (_, v) => {
    if (v !== null) setSettings(s => ({ ...s, [key]: v }))
  }

  const toggle = (key) => () => setSettings(s => ({ ...s, [key]: !s[key] }))

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Export Timeline</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Configure what appears in the exported PDF. These settings do not affect the live timeline.
          </Typography>

          <Stack spacing={2}>
            {/* Row 1: Group + Sort + View */}
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 500 }}>
                  Group
                </Typography>
                <TpToggleButtonGroup exclusive value={settings.groupStrategy} onChange={set('groupStrategy')}>
                  <TpToggleButton value="none">None</TpToggleButton>
                  <TpToggleButton value="species">Species</TpToggleButton>
                  <TpToggleButton value="location">Location</TpToggleButton>
                  <TpToggleButton value="family">Family</TpToggleButton>
                </TpToggleButtonGroup>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 500 }}>
                  Sort
                </Typography>
                <TpToggleButtonGroup exclusive value={settings.sortStrategy} onChange={set('sortStrategy')} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                  <TpToggleButton value="alphabetical">
                    <Icons.SortByAlpha sx={{ fontSize: 16 }} />
                  </TpToggleButton>
                  <TpToggleButton value="chronological">
                    <Icons.Today sx={{ fontSize: 16 }} />
                  </TpToggleButton>
                </TpToggleButtonGroup>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 500 }}>
                  View
                </Typography>
                <TpToggleButtonGroup exclusive value={settings.viewStrategy} onChange={set('viewStrategy')} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                  <TpToggleButton value="phases">Phases</TpToggleButton>
                  <TpToggleButton value="tasks">Tasks</TpToggleButton>
                  <TpToggleButton value="both">Both</TpToggleButton>
                </TpToggleButtonGroup>
              </Box>
            </Stack>

            {/* Row 2: Org Tasks + Successions */}
            <Stack direction="row" spacing={3}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 500 }}>
                  Org Tasks
                </Typography>
                <TpToggleButton
                  selected={settings.showOrgTasks}
                  onClick={toggle('showOrgTasks')}
                  sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                >
                  {settings.showOrgTasks
                    ? <Icons.Home sx={{ fontSize: 20 }} />
                    : <Icons.HomeOutlined sx={{ fontSize: 20 }} />
                  }
                </TpToggleButton>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 500 }}>
                  Successions
                </Typography>
                <TpToggleButton
                  selected={settings.showSuccessions}
                  onClick={toggle('showSuccessions')}
                  sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                >
                  <Icons.Repeat sx={{ fontSize: 20 }} />
                </TpToggleButton>
              </Box>
            </Stack>
          </Stack>

          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
            <Button onClick={onClose} variant="outlined" disabled={isExporting}>
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              variant="contained"
              disabled={isExporting || !ready}
              startIcon={isExporting
                ? <CircularProgress size={16} color="inherit" />
                : !ready
                ? <CircularProgress size={16} color="inherit" />
                : <Icons.PictureAsPdf />
              }
            >
              {isExporting ? 'Exporting…' : !ready ? 'Preparing…' : 'Export PDF'}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* Hidden export render — portalled to document.body, off-screen */}
      {open && createPortal(
        <Box
          ref={exportRef}
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            width: EXPORT_WIDTH_PX,
            opacity: 0,
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 9999,
          }}
        >
          <CropTimeline
            printMode
            hideNowLine
            hideToggleButtons
            autoFitZoom
            interactive={false}
            hideBadges
            initialStateOverride={{
              groupStrategy: settings.groupStrategy,
              sortStrategy: settings.sortStrategy,
              viewStrategy: settings.viewStrategy,
              showOrgTasks: settings.showOrgTasks,
              showSuccessions: settings.showSuccessions,
              expandedIds: [],
              containerWidth: EXPORT_WIDTH_PX,
            }}
          />
        </Box>,
        document.body
      )}
    </>
  )
}
